/** Columns for the Find & Replace results grid.
 *
 * A row is one FIELD, not one record, so the grid leads with what was matched
 * (snippet) and where (kind · record · field) rather than with a record identity.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import type { ContentSearchRow } from '@/api/contentSearch';
import { TimestampsCell } from '@/components/DataGrid/TimestampsCell';

import { RecordLink } from './RecordLink';

/** The matched fragment with its surrounding context, match highlighted.
 *
 *  Rendered as React nodes rather than markup — `react/no-danger` is an ESLint
 *  error in this app, and this is operator-authored template source, which is
 *  exactly the content you must never inject as HTML. */
function Snippet({ row }: { row: ContentSearchRow }) {
  const first = row.snippets[0];
  if (!first) return <Typography variant="body2">—</Typography>;
  const extra = row.hits > 1 ? ` +${row.hits - 1} more` : '';
  return (
    <Box sx={{ py: 0.75, minWidth: 0 }}>
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {first.before}
        </Box>
        <Box
          component="mark"
          sx={{
            bgcolor: 'warning.light',
            color: 'warning.contrastText',
            px: 0.25,
            borderRadius: 0.5,
          }}
        >
          {first.match}
        </Box>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {first.after}
        </Box>
      </Typography>
      {extra && (
        <Typography variant="caption" color="text.secondary">
          {extra.trim()}
        </Typography>
      )}
    </Box>
  );
}

export function buildContentColumns(): GridColDef<ContentSearchRow>[] {
  return [
    {
      field: 'kind_label',
      headerName: 'Type',
      width: 130,
      sortable: false,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) => (
        <Chip size="small" variant="outlined" label={p.row.kind_label} />
      ),
    },
    {
      field: 'label',
      headerName: 'Record',
      flex: 1,
      minWidth: 200,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) => (
        <RecordLink link={p.row.link} label={p.row.label} kindLabel={p.row.kind_label} />
      ),
    },
    {
      field: 'slot_label',
      headerName: 'Field',
      width: 170,
      sortable: false,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {p.row.slot_label}
          </Typography>
          {!p.row.replaceable && (
            <Tooltip
              title={
                'Identity fields are searchable but not replaceable — renaming can break ' +
                'references and unique constraints.'
              }
            >
              <Typography variant="caption" color="text.secondary">
                read-only
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'tenant',
      headerName: 'Tenant',
      width: 130,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) => (
        <span>{p.row.tenant ?? 'system'}</span>
      ),
    },
    {
      field: 'hits',
      headerName: 'Hits',
      width: 80,
      type: 'number',
    },
    {
      field: 'snippet',
      headerName: 'Match',
      flex: 2,
      minWidth: 320,
      sortable: false,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) => <Snippet row={p.row} />,
    },
    {
      field: 'updated',
      headerName: 'Updated',
      width: 170,
      renderCell: (p: GridRenderCellParams<ContentSearchRow>) =>
        p.row.updated ? <TimestampsCell created={null} updated={p.row.updated} /> : <span>—</span>,
    },
  ];
}
